import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Radar, Share2, Sparkles, UserCheck, MessageSquare, Calendar, Handshake,
  ShieldCheck, Users, Mail, Database, Globe, Facebook, MessageCircle, Instagram,
  MessagesSquare, Moon, ArrowRight, Zap, Clock, CheckCircle2, Rocket, Target, Play,
} from "lucide-react";
import { HeroFilmLoop, AgentFilmPlayer } from "./AgentFilm";

const GOLD = "#C19A6B";

const HERO_IMG = "https://images.unsplash.com/photo-1455593984172-9f753a2e1ebd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const RECAP_IMG = "https://images.unsplash.com/photo-1483389127117-b6a2102724ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.04 } } };
const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } };
const reveal = { viewport: { once: true, amount: 0.25 }, initial: "hidden", whileInView: "show" };

const PIPELINE = [
  { step: "01", name: "VOS BIENS", icon: Home, desc: "Vos mandats simples & exclusifs" },
  { step: "02", name: "RADAR ACQUÉREURS IA", icon: Radar, desc: "Scan continu des signaux d'achat" },
  { step: "03", name: "MULTI-CANAUX", icon: Share2, desc: "Off Markette · CRM · Web · Partenaires · Social" },
  { step: "04", name: "MATCHING IA", icon: Sparkles, desc: "Rapprochement des bons profils" },
  { step: "05", name: "ACQUÉREURS QUALIFIÉS", icon: UserCheck, desc: "Critères & budgets confirmés" },
  { step: "06", name: "PRISE DE CONTACT", icon: MessageSquare, desc: "Le bon canal, préparé pour vous" },
  { step: "07", name: "VISITES", icon: Calendar, desc: "Rendez-vous planifiés" },
  { step: "08", name: "VENTES", icon: Handshake, desc: "Concrétisation" },
];

const TRIGGERS = [
  { title: "Moulinettes automatiques & manuelles", desc: "Le Radar tourne en continu et à la demande." },
  { title: "Boost Mon Bien", desc: "Concentration maximale du Radar sur un mandat." },
  { title: "Nouveaux biens enregistrés", desc: "Matching rétroactif instantané sur la base acquéreurs." },
  { title: "Nouveaux matchs internes", desc: "Détection immédiate des synergies du réseau." },
];

const CHANNELS = [
  { key: "off_markette", name: "OFF MARKETTE", status: "Actif", active: true, icon: ShieldCheck, desc: "Matcher instantanément avec notre base acquéreurs qualifiée." },
  { key: "partenaires", name: "RÉSEAU PARTENAIRES", status: "Actif", active: true, icon: Users, desc: "Trouver l'acquéreur chez un autre conseiller du réseau." },
  { key: "email", name: "EMAIL DÉDIÉ", status: "Actif", active: true, icon: Mail, desc: "Présenter automatiquement le bon bien au bon prospect." },
  { key: "crm", name: "VOTRE CRM", status: "Actif", active: true, icon: Database, desc: "Réactiver les acquéreurs et contacts déjà connus." },
  { key: "web", name: "SOURCES WEB", status: "Actif", active: true, icon: Globe, desc: "Identifier de nouvelles recherches sur les sources publiques autorisées." },
  { key: "facebook", name: "FACEBOOK", status: "À connecter", active: false, icon: Facebook, desc: "Détecter des intentions d'achat accessibles sur les espaces autorisés." },
  { key: "whatsapp", name: "WHATSAPP", status: "À connecter", active: false, icon: MessageCircle, desc: "Transformer les prospects autorisés en conversations." },
  { key: "instagram", name: "INSTAGRAM", status: "À connecter", active: false, icon: Instagram, desc: "Capturer les interactions accessibles via les outils Meta." },
  { key: "communautes", name: "FORUMS & COMMUNAUTÉS", status: "Bientôt", active: false, icon: MessagesSquare, desc: "Repérer de nouvelles intentions sur les espaces accessibles." },
];

function StatusBadge({ active, status }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${active ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-amber-500"}`} />
      {status}
    </span>
  );
}

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <motion.div variants={fadeUp} className="max-w-3xl">
      {eyebrow && <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C19A6B] mb-2">{eyebrow}</div>}
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] leading-tight">{title}</h2>
      {sub && <p className="calli text-2xl sm:text-3xl text-[#C19A6B] mt-1">{sub}</p>}
    </motion.div>
  );
}

export default function DecouvrirAgentIA({ setTab, member }) {
  const howRef = useRef(null);
  const [showFilm, setShowFilm] = useState(false);
  const cfgName = (member && member.agent_config && member.agent_config.agent_name || "").trim();
  const emma = cfgName && cfgName !== "Votre Agent IA" ? cfgName : "Emma";
  const activate = () => setTab && setTab("agent_settings");
  const scrollHow = () => { try { howRef.current && howRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_) {} };

  return (
    <div data-testid="decouvrir-agent-ia" className="space-y-16 sm:space-y-24 pb-10">

      {/* 1. HERO */}
      <motion.section variants={container} initial="hidden" animate="show" className="relative overflow-hidden rounded-3xl">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A]/95 via-[#141210]/92 to-[#2A2219]/85" />
        </div>
        <div className="relative px-6 sm:px-10 lg:px-14 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-[minmax(0,38fr)_minmax(0,62fr)] gap-10 lg:gap-14 items-center">
          <div className="max-w-md">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#E8D8C3] bg-white/5 border border-[#C19A6B]/25 rounded-full px-3 py-1 mb-5">
              <Moon className="w-3 h-3 text-[#C19A6B]" /> Votre prospecteur immobilier IA
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-[2.4rem] font-semibold tracking-tight text-[#f5f5f5] leading-[1.15] [text-shadow:_0_2px_16px_rgba(0,0,0,0.55)]">
              Votre prospection continue<br /><span className="text-[#E4C79E]">même quand vous dormez.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="calli text-lg sm:text-xl text-[#E4C79E] mt-1.5 [text-shadow:_0_1px_10px_rgba(0,0,0,0.5)]">Votre prospecteur ne dort jamais.</motion.p>
            <motion.p variants={fadeUp} className="text-sm text-white/75 leading-relaxed max-w-sm mt-4 [text-shadow:_0_1px_8px_rgba(0,0,0,0.5)]">
              {emma} analyse votre portefeuille et cherche des acquéreurs en continu — sur Off Markette et les canaux autorisés.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-2.5 mt-7">
              <button onClick={activate} data-testid="activate-emma-hero-btn" className="inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#A8865C] text-white text-sm font-semibold py-3 px-6 rounded-xl shadow-lg shadow-[#C19A6B]/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl">
                <Zap className="w-4 h-4" /> Activer mon Agent IA
              </button>
              <button onClick={() => setShowFilm(true)} data-testid="how-it-works-hero-btn" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 text-sm font-semibold py-3 px-6 rounded-xl transition-all duration-300 hover:-translate-y-0.5">
                <Play className="w-3.5 h-3.5 fill-white" /> Voir le film
              </button>
            </motion.div>
            <motion.button variants={fadeUp} onClick={scrollHow} className="text-[12px] text-white/60 hover:text-[#E4C79E] mt-3.5 inline-flex items-center gap-1 transition-colors" data-testid="how-it-works-scroll">Voir comment ça marche <ArrowRight className="w-3 h-3" /></motion.button>
          </div>

          {/* Film Hero (preview premium) */}
          <div className="relative">
            <HeroFilmLoop onPlay={() => setShowFilm(true)} />
          </div>
        </div>
      </motion.section>

      {/* 2. PIPELINE */}
      <motion.section ref={howRef} variants={container} {...reveal} className="scroll-mt-32">
        <SectionTitle eyebrow="L'agent au travail" title="Du mandat à la vente, sans interruption." sub="Un pipeline intelligent et ininterrompu" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-8">
          {PIPELINE.map((n, i) => (
            <motion.div key={n.step} variants={fadeUp} data-testid={`pipeline-step-${n.step}`}
              className="relative bg-white rounded-2xl border border-zinc-100 p-4 sm:p-5 hover:border-[#C19A6B]/40 hover:shadow-[0_0_18px_rgba(193,154,107,0.12)] transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#C19A6B]/12 flex items-center justify-center text-[#C19A6B]"><n.icon className="w-5 h-5" /></div>
                <span className="text-[11px] font-bold text-zinc-300">{n.step}</span>
              </div>
              <div className="text-sm font-bold text-[#111827] leading-tight">{n.name}</div>
              <div className="text-[12px] text-zinc-500 mt-1 leading-snug">{n.desc}</div>
              {i < PIPELINE.length - 1 && <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C19A6B]/40 z-10" />}
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* 3. RADAR EXPLAINER */}
      <motion.section variants={container} {...reveal}>
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-center">
          <motion.div variants={fadeUp}>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C19A6B] mb-3"><Radar className="w-4 h-4" /> Le moteur</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] leading-tight">Le Radar Acquéreurs IA</h2>
            <p className="text-base text-zinc-600 leading-relaxed mt-3 max-w-md">
              Votre Radar analyse vos mandats et recherche les signaux d'achat susceptibles de correspondre à vos biens. Il ne dort jamais.
            </p>
          </motion.div>
          <motion.div variants={container} className="grid sm:grid-cols-2 gap-3">
            {TRIGGERS.map((t) => (
              <motion.div key={t.title} variants={fadeUp} className="bg-[#FDFBF7] rounded-2xl border border-[#E8D8C3] p-4">
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-[#C19A6B]/15 flex items-center justify-center shrink-0"><Target className="w-4 h-4 text-[#C19A6B]" /></div>
                  <div>
                    <div className="text-sm font-semibold text-[#111827] leading-snug">{t.title}</div>
                    <div className="text-[12.5px] text-zinc-500 mt-0.5 leading-snug">{t.desc}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* 4. CHANNELS */}
      <motion.section variants={container} {...reveal}>
        <SectionTitle eyebrow="Couverture omnicanale" title="Partout où il peut trouver une opportunité, votre Agent IA est à l'affût." sub="Ciblée, conforme, jamais intrusive" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {CHANNELS.map((c) => (
            <motion.div key={c.key} variants={fadeUp} data-testid={`channel-card-${c.key}`}
              className={`relative bg-white rounded-2xl border p-5 transition-all hover:-translate-y-0.5 ${c.active ? "border-zinc-100 hover:border-[#C19A6B]/40 hover:shadow-md" : "border-zinc-100 opacity-95"}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.active ? "bg-[#C19A6B]/12 text-[#C19A6B]" : "bg-zinc-100 text-zinc-400"}`}><c.icon className="w-5 h-5" /></div>
                <StatusBadge active={c.active} status={c.status} />
              </div>
              <div className="text-sm font-bold text-[#111827] tracking-tight">{c.name}</div>
              <div className="text-[13px] text-zinc-500 mt-1.5 leading-snug">{c.desc}</div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* 5a. PENDANT QUE VOUS VENDEZ */}
      <motion.section variants={container} {...reveal} className="relative overflow-hidden rounded-3xl bg-[#0A0A0A]">
        <div className="relative px-6 sm:px-12 py-14 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div variants={fadeUp}>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">PENDANT QUE VOUS VENDEZ,<br /><span className="text-[#E4C79E]">{emma.toUpperCase()} PROSPECTE.</span></h2>
            <p className="text-zinc-300 mt-4 max-w-md leading-relaxed">Vous restez l'agent immobilier référent. {emma} devient votre force de prospection augmentée.</p>
          </motion.div>
          <motion.div variants={container} className="grid grid-cols-2 gap-2.5">
            {["En visite", "En estimation", "En négociation", "En signature", "En famille", "En train de dormir"].map((mmt) => (
              <motion.div key={mmt} variants={fadeUp} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3.5 py-3">
                <CheckCircle2 className="w-4 h-4 text-[#C19A6B] shrink-0" />
                <span className="text-sm text-zinc-200">{mmt}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* 5b. 8H DE TRAVAIL */}
      <motion.section variants={container} {...reveal} className="text-center max-w-3xl mx-auto">
        <motion.div variants={fadeUp} className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C19A6B] mb-3"><Clock className="w-4 h-4" /> Sans horaires</motion.div>
        <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] leading-tight">8H DE TRAVAIL PAR JOUR ?<br /><span className="text-[#C19A6B]">Pas pour votre Agent IA.</span></motion.h2>
        <motion.p variants={fadeUp} className="text-base text-zinc-600 leading-relaxed mt-4">
          Pendant que vous consacrez votre énergie aux estimations, à la relation propriétaires, aux visites et à la négociation… <b className="text-[#111827]">{emma} cherche.</b>
        </motion.p>
      </motion.section>

      {/* 6. MORNING RECAP TEASER */}
      <motion.section variants={container} {...reveal}>
        <SectionTitle eyebrow="Preuve de travail" title="Chaque matin, découvrez le travail de la nuit." sub="Emma a travaillé pour vous" />
        <motion.div variants={fadeUp} data-testid="morning-recap-card" className="mt-8 relative overflow-hidden rounded-3xl border border-[#E8D8C3]">
          <div className="grid md:grid-cols-2">
            <div className="relative min-h-[220px]">
              <img src={RECAP_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="inline-flex items-center gap-1.5 bg-black/40 backdrop-blur px-2.5 py-1 rounded-full border border-white/15 text-[10px] text-zinc-200 uppercase tracking-wider font-semibold">Exemple illustratif</div>
              </div>
            </div>
            <div className="bg-[#FDFBF7] p-7 sm:p-9">
              <div className="flex items-center gap-2 text-[#C19A6B] mb-1"><Sparkles className="w-4 h-4" /><span className="text-[11px] font-semibold uppercase tracking-[0.2em]">Votre point du matin</span></div>
              <div className="calli text-2xl sm:text-3xl text-[#111827] mb-4">« {emma} a travaillé pour vous cette nuit. »</div>
              <div className="grid grid-cols-3 gap-3">
                {[["142", "Signaux analysés"], ["12", "Matchs potentiels"], ["3", "Prêts à visiter"]].map(([v, l]) => (
                  <div key={l} className="bg-white rounded-xl border border-[#E8D8C3] p-3 text-center">
                    <div className="text-2xl font-extrabold text-[#C19A6B]">{v}</div>
                    <div className="text-[11px] text-zinc-500 leading-tight mt-0.5">{l}</div>
                  </div>
                ))}
              </div>
              <p className="text-[12px] text-zinc-400 mt-4 leading-snug">Sur votre tableau de bord, {emma} n'affiche que de vraies données issues de votre activité réelle.</p>
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* 7. CLOSING MANIFESTO */}
      <motion.section variants={container} {...reveal} className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A0A0A] via-[#141210] to-[#2A2219] text-center">
        <div className="relative px-6 sm:px-12 py-16 sm:py-20">
          <motion.div variants={fadeUp} className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#f5f5f5] leading-snug max-w-3xl mx-auto">
            VOS BIENS SONT EN LIGNE.<br /><span className="text-[#E4C79E]">VOTRE AGENT IA, LUI, EST DÉJÀ PARTI CHERCHER LES ACQUÉREURS.</span>
          </motion.div>
          <motion.p variants={fadeUp} className="text-white/75 mt-6 max-w-xl mx-auto leading-relaxed">
            Les portails immobiliers attendent les acquéreurs. <b className="text-[#f5f5f5]">Off Markette va les chercher.</b>
          </motion.p>
          <motion.div variants={fadeUp} className="calli text-3xl sm:text-4xl text-[#E4C79E] mt-8">Même quand vous dormez, votre prospection continue.</motion.div>
          <motion.button variants={fadeUp} onClick={activate} data-testid="activate-emma-final-btn" className="inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#A8865C] text-white font-semibold py-4 px-10 rounded-xl shadow-lg transition-all hover:-translate-y-0.5 text-base mt-8">
            <Rocket className="w-5 h-5" /> ACTIVER MON AGENT IA
          </motion.button>
        </div>
      </motion.section>

      {/* Compliance */}
      <motion.p {...reveal} variants={fadeUp} className="flex items-start gap-2 text-[12px] text-zinc-400 leading-snug max-w-3xl mx-auto text-center justify-center">
        <ShieldCheck className="w-4 h-4 text-[#C19A6B] shrink-0 mt-0.5" />
        {emma} recherche les opportunités accessibles via les sources et intégrations autorisées, dans le respect strict du RGPD et des règles de chaque plateforme.
      </motion.p>

      <AnimatePresence>
        {showFilm && <AgentFilmPlayer onClose={() => setShowFilm(false)} onActivate={() => { setShowFilm(false); activate(); }} />}
      </AnimatePresence>
    </div>
  );
}
